import React from 'react';
import { Palette, Search, Paintbrush, Rocket, Power, ExternalLink } from 'lucide-react';
import { cn } from '../../utils/cn';
import { getPublishedSiteUrl, getPublishedSiteHostDisplay } from '../../utils/publishedUrl';
import type { Site } from '../../schemas/validation';

interface WebsiteInfoSectionProps {
  site: Site;
  onThemeClick: () => void;
  onSeoClick: () => void;
  onBrandPackageClick?: () => void;
  brandPackageName?: string | null;
  onPublishClick: () => void;
  isPublishing: boolean;
}

export const WebsiteInfoSection: React.FC<WebsiteInfoSectionProps> = ({
  site,
  onThemeClick,
  onSeoClick,
  onBrandPackageClick,
  brandPackageName,
  onPublishClick,
  isPublishing,
}) => {
  const handleAccessWebsite = () => {
    if (site.isPublished && (site.subdomain || site.customDomain)) {
      window.open(getPublishedSiteUrl(site), '_blank');
    }
  };

  const publishedHost = getPublishedSiteHostDisplay(site);

  return (
    <div className="mb-8">
      <div className="card p-4 sm:p-5">
        {/* Row 1: Status + primary actions */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div
              className={cn(
                'w-2.5 h-2.5 rounded-full shrink-0',
                site.isPublished ? 'bg-green-500' : 'bg-gray-400'
              )}
            />
            <span
              className={cn(
                'text-sm font-medium shrink-0',
                site.isPublished ? 'text-green-700' : 'text-gray-600'
              )}
            >
              {site.isPublished ? 'Live' : 'Offline'}
            </span>
            {publishedHost && (
              <span className="text-sm text-gray-500 font-mono truncate">{publishedHost}</span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {site.isPublished && (site.subdomain || site.customDomain) && (
              <button
                onClick={handleAccessWebsite}
                className="inline-flex items-center px-3 py-1.5 border border-purple-300 shadow-sm text-xs font-medium rounded-md text-purple-700 bg-white hover:bg-purple-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors"
              >
                <ExternalLink className="h-3 w-3 mr-1.5" />
                View Live
              </button>
            )}
            <button
              onClick={onPublishClick}
              disabled={isPublishing}
              className={cn(
                'inline-flex items-center px-3 py-1.5 border shadow-sm text-xs font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2',
                site.isPublished
                  ? 'border-red-300 text-red-700 bg-white hover:bg-red-50 focus:ring-red-500'
                  : 'border-green-300 text-green-700 bg-white hover:bg-green-50 focus:ring-green-500',
                isPublishing && 'opacity-50 cursor-not-allowed'
              )}
            >
              {isPublishing ? (
                <>
                  <div className="w-3 h-3 mr-1.5 animate-spin border-2 border-current border-t-transparent rounded-full" />
                  {site.isPublished ? 'Taking offline...' : 'Going live...'}
                </>
              ) : site.isPublished ? (
                <>
                  <Power className="h-3 w-3 mr-1.5" />
                  Take Offline
                </>
              ) : (
                <>
                  <Rocket className="h-3 w-3 mr-1.5" />
                  Go Live
                </>
              )}
            </button>
          </div>
        </div>

        {/* Row 2: Design controls (left) + SEO (right) */}
        <div className="flex flex-wrap items-center justify-between gap-3 mt-3 pt-3 border-t border-gray-100">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Theme:</span>
              <span className="text-xs font-medium text-gray-900">
                {site.theme ? site.theme.charAt(0).toUpperCase() + site.theme.slice(1) : 'Standard'}
              </span>
              <button
                onClick={onThemeClick}
                className="inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary-500 transition-colors"
              >
                <Palette className="h-3 w-3 mr-1" />
                Change
              </button>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Brand:</span>
              <span className="text-xs font-medium text-gray-900">
                {brandPackageName || <span className="text-gray-400 italic">Theme colors</span>}
              </span>
              {onBrandPackageClick && (
                <button
                  onClick={onBrandPackageClick}
                  className="inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary-500 transition-colors"
                >
                  <Paintbrush className="h-3 w-3 mr-1" />
                  {brandPackageName ? 'Change' : 'Select'}
                </button>
              )}
            </div>
          </div>

          <button
            onClick={onSeoClick}
            className="inline-flex items-center px-2.5 py-1 border border-indigo-300 shadow-sm text-xs font-medium rounded-md text-indigo-700 bg-white hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-500 transition-colors"
          >
            <Search className="h-3 w-3 mr-1" />
            SEO & Analytics
          </button>
        </div>
      </div>
    </div>
  );
};
